import { useState, useCallback, useEffect } from "react";
import { SavedCalculation } from "@/types/cvr";
import { useToast } from "@/hooks/use-toast";
import { fetchCVRCalculations, deleteCVRCalculation, renameCVRCalculation } from "@/lib/supabase/cvr";
import { fetchEVMCalculations, deleteEVMCalculation, renameEVMCalculation } from "@/lib/supabase/evm";
import { fetchEarlyWarnings, deleteEarlyWarning, renameEarlyWarning } from "@/lib/supabase/early-warning";
import { fetchTargetCostCalculations, deleteTargetCostCalculation, renameTargetCostCalculation } from "@/lib/supabase/target-cost";
import { fetchFatigueCalculations, deleteFatigueCalculation, renameFatigueCalculation } from "@/lib/supabase/fatigue";
import { useSupabaseSubscription } from "./useSupabaseSubscription";
import { useAuth } from "@/context/AuthContext";
import { captureEvent, EVENTS, TOOL_IDS, TOOL_NAMES } from "@/lib/posthog";

export function useCalculations(type: "cvr_calculations" | "evm_calculations" | "early_warning_register" | "target_cost_calculations" | "fatigue_calculations" = "cvr_calculations") {
  const [calculations, setCalculations] = useState<SavedCalculation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [activeCalculationId, setActiveCalculationId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const loadCalculations = useCallback(async () => {
    if (!user) {
      setCalculations([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      let data;
      
      switch (type) {
        case "evm_calculations":
          data = await fetchEVMCalculations();
          break;
        case "fatigue_calculations":
          data = await fetchFatigueCalculations();
          break;
        case "early_warning_register":
          data = await fetchEarlyWarnings();
          break;
        case "target_cost_calculations":
          data = await fetchTargetCostCalculations();
          break;
        default:
          data = await fetchCVRCalculations();
      }
      
      setCalculations(data);
    } catch (err) {
      console.error(`Error fetching ${type} calculations:`, err);
      const error = err instanceof Error ? err : new Error("Failed to fetch calculations");
      setError(error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, type]);

  // Alias for loadCalculations to maintain API compatibility
  const refreshCalculations = loadCalculations;

  const addCalculation = useCallback((calculation: SavedCalculation) => {
    setCalculations(prev => [calculation, ...prev]);
  }, []);

  const updateCalculation = useCallback((id: string, updatedData: Partial<SavedCalculation>) => {
    setCalculations(prev =>
      prev.map(calc => calc.id === id ? { ...calc, ...updatedData } : calc)
    );
  }, []);

  const removeCalculation = useCallback((id: string) => {
    setCalculations(prev => prev.filter(calc => calc.id !== id));
  }, []);

  const deleteCalculation = useCallback(async (id: string, projectName?: string) => {
    switch (type) {
      case "evm_calculations":
        await deleteEVMCalculation(id);
        captureEvent(EVENTS.EVM_CALCULATION_DELETED, {
          tool_id: TOOL_IDS.EVM,
          tool_name: TOOL_NAMES[TOOL_IDS.EVM],
          calculation_id: id,
          project_name: projectName,
        });
        break;
      case "fatigue_calculations":
        await deleteFatigueCalculation(id);
        break;
      case "early_warning_register":
        await deleteEarlyWarning(id);
        captureEvent(EVENTS.EARLY_WARNING_DELETED, {
          tool_id: TOOL_IDS.EARLY_WARNING,
          tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
          calculation_id: id,
          project_name: projectName,
        });
        break;
      case "target_cost_calculations":
        await deleteTargetCostCalculation(id);
        captureEvent(EVENTS.TARGET_COST_DELETED, {
          tool_id: TOOL_IDS.TARGET_COST,
          tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
          calculation_id: id,
          project_name: projectName,
        });
        break;
      default:
        await deleteCVRCalculation(id);
        captureEvent(EVENTS.CVR_CALCULATION_DELETED, {
          tool_id: TOOL_IDS.CVR,
          tool_name: TOOL_NAMES[TOOL_IDS.CVR],
          calculation_id: id,
          project_name: projectName,
        });
    }
    removeCalculation(id);
  }, [type, removeCalculation]);

  const renameCalculation = useCallback(async (id: string, newName: string, oldName?: string) => {
    switch (type) {
      case "evm_calculations":
        await renameEVMCalculation(id, newName);
        captureEvent(EVENTS.EVM_CALCULATION_RENAMED, {
          tool_id: TOOL_IDS.EVM,
          tool_name: TOOL_NAMES[TOOL_IDS.EVM],
          calculation_id: id,
          old_name: oldName,
          new_name: newName,
        });
        break;
      case "fatigue_calculations":
        await renameFatigueCalculation(id, newName);
        break;
      case "early_warning_register":
        await renameEarlyWarning(id, newName);
        captureEvent(EVENTS.EARLY_WARNING_RENAMED, {
          tool_id: TOOL_IDS.EARLY_WARNING,
          tool_name: TOOL_NAMES[TOOL_IDS.EARLY_WARNING],
          calculation_id: id,
          old_name: oldName,
          new_name: newName,
        });
        break;
      case "target_cost_calculations":
        await renameTargetCostCalculation(id, newName);
        captureEvent(EVENTS.TARGET_COST_RENAMED, {
          tool_id: TOOL_IDS.TARGET_COST,
          tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
          calculation_id: id,
          old_name: oldName,
          new_name: newName,
        });
        break;
      default:
        await renameCVRCalculation(id, newName);
        captureEvent(EVENTS.CVR_CALCULATION_RENAMED, {
          tool_id: TOOL_IDS.CVR,
          tool_name: TOOL_NAMES[TOOL_IDS.CVR],
          calculation_id: id,
          old_name: oldName,
          new_name: newName,
        });
    }
    updateCalculation(id, { project_name: newName });
  }, [type, updateCalculation]);

  useEffect(() => {
    loadCalculations();
  }, [loadCalculations]);

  useSupabaseSubscription(type, loadCalculations);

  return {
    calculations,
    setCalculations,
    isLoading,
    error,
    loadCalculations,
    refreshCalculations,
    addCalculation,
    updateCalculation,
    removeCalculation,
    deleteCalculation,
    renameCalculation,
    activeCalculationId,
    setActiveCalculationId,
  };
}
